import { useEffect, useRef } from 'react';

type CallbackFunction = (() => void) | (() => Promise<void>);
/**
 * Source: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 *
 * Advantages of this hook over the standard `setInterval` function:
 * 1. Automatic cleanup with `clearInterval`
 * 2. Variable delay instead of a constant
 * @param {Function} callback - function to be called at every interval
 * @param {number} delay - time between each interval
 */
const useInterval = (callback: CallbackFunction, delay: number) => {
  const callbackRef = useRef<CallbackFunction>();

  // update callback every render
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === 0) {
      return;
    }

    const id = setInterval(() => {
      if (callbackRef.current) {
        const promise = callbackRef.current();

        if (typeof promise !== 'undefined') {
          promise.catch(() => {
            clearInterval(id);
          });
        }
      }
    }, delay);

    return () => clearInterval(id);
  }, [delay]);
};

export default useInterval;
